import {PayloadAction, createSlice} from '@reduxjs/toolkit'
import {ZadCustomerRefund} from '../../../models/zad'
import {ZadApiError} from '../zad/errors'
import {
  cancelZadCustomerRefund,
  createZadCustomerRefund,
  fetchZadCustomerRefundById,
  fetchZadCustomerRefundPickerCustomers,
  fetchZadCustomerRefundPickerReturns,
  fetchZadCustomerRefunds,
  fetchZadCustomerRefundSummary,
} from './service'
import {FetchZadCustomerRefundsResponse, ZadCustomerRefundState} from './types'

const initialState: ZadCustomerRefundState = {
  refunds: [],
  total: 0,
  totalPages: 0,
  listLoading: false,
  listInitialized: false,
  actionLoading: false,
  selectedRefund: null,
  customerSummary: null,
  pickerCustomers: [],
  pickerReturns: [],
  pickerLoading: false,
  error: null,
  errorCode: null,
  errorStatus: null,
}

function applyRejected(state: ZadCustomerRefundState, action: PayloadAction<any>) {
  const payload = (action.payload || {}) as ZadApiError
  state.error = payload.error || 'Request failed'
  state.errorCode = payload.errorCode || null
  state.errorStatus = payload.status || null
}

function clearError(state: ZadCustomerRefundState) {
  state.error = null
  state.errorCode = null
  state.errorStatus = null
}

function upsertRefund(state: ZadCustomerRefundState, refund?: ZadCustomerRefund | null) {
  if (!refund?._id) return
  state.selectedRefund = refund
  const index = state.refunds.findIndex((item) => item._id === refund._id)
  if (index >= 0) {
    state.refunds[index] = refund
  }
}

const zadCustomerRefundSlice = createSlice({
  name: 'zadCustomerRefunds',
  initialState,
  reducers: {
    clearZadCustomerRefundError: (state) => {
      clearError(state)
    },
    clearSelectedZadCustomerRefund: (state) => {
      state.selectedRefund = null
    },
    clearZadCustomerRefundSummary: (state) => {
      state.customerSummary = null
    },
    clearZadCustomerRefundPickerReturns: (state) => {
      state.pickerReturns = []
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchZadCustomerRefunds.pending, (state) => {
      state.listLoading = true
      clearError(state)
    })
    builder.addCase(fetchZadCustomerRefunds.rejected, (state, action: PayloadAction<any>) => {
      applyRejected(state, action)
      state.listLoading = false
      state.listInitialized = true
    })
    builder.addCase(
      fetchZadCustomerRefunds.fulfilled,
      (state, action: PayloadAction<FetchZadCustomerRefundsResponse>) => {
        state.listLoading = false
        state.listInitialized = true
        clearError(state)
        state.refunds = action.payload.refunds
        state.total = action.payload.total
        state.totalPages = action.payload.totalPages
      }
    )

    builder.addCase(fetchZadCustomerRefundById.pending, (state) => {
      state.actionLoading = true
    })
    builder.addCase(fetchZadCustomerRefundById.rejected, (state, action: PayloadAction<any>) => {
      applyRejected(state, action)
      state.actionLoading = false
    })
    builder.addCase(fetchZadCustomerRefundById.fulfilled, (state, action) => {
      state.actionLoading = false
      state.selectedRefund = action.payload.refund
    })

    builder.addCase(createZadCustomerRefund.pending, (state) => {
      state.actionLoading = true
      clearError(state)
    })
    builder.addCase(createZadCustomerRefund.rejected, (state, action: PayloadAction<any>) => {
      applyRejected(state, action)
      state.actionLoading = false
    })
    builder.addCase(createZadCustomerRefund.fulfilled, (state, action) => {
      state.actionLoading = false
      clearError(state)
      upsertRefund(state, action.payload.refund)
    })

    builder.addCase(cancelZadCustomerRefund.pending, (state) => {
      state.actionLoading = true
      clearError(state)
    })
    builder.addCase(cancelZadCustomerRefund.rejected, (state, action: PayloadAction<any>) => {
      applyRejected(state, action)
      state.actionLoading = false
    })
    builder.addCase(cancelZadCustomerRefund.fulfilled, (state, action) => {
      state.actionLoading = false
      clearError(state)
      upsertRefund(state, action.payload.refund)
    })

    builder.addCase(fetchZadCustomerRefundSummary.fulfilled, (state, action) => {
      state.customerSummary = action.payload.summary
    })
    builder.addCase(fetchZadCustomerRefundSummary.rejected, (state) => {
      state.customerSummary = null
    })

    builder.addCase(fetchZadCustomerRefundPickerCustomers.pending, (state) => {
      state.pickerLoading = true
    })
    builder.addCase(fetchZadCustomerRefundPickerCustomers.rejected, (state) => {
      state.pickerLoading = false
    })
    builder.addCase(fetchZadCustomerRefundPickerCustomers.fulfilled, (state, action) => {
      state.pickerLoading = false
      state.pickerCustomers = action.payload.customers
    })

    builder.addCase(fetchZadCustomerRefundPickerReturns.pending, (state) => {
      state.pickerLoading = true
    })
    builder.addCase(fetchZadCustomerRefundPickerReturns.rejected, (state) => {
      state.pickerLoading = false
    })
    builder.addCase(fetchZadCustomerRefundPickerReturns.fulfilled, (state, action) => {
      state.pickerLoading = false
      state.pickerReturns = action.payload.returns
    })
  },
})

export default zadCustomerRefundSlice.reducer
export const actions = zadCustomerRefundSlice.actions
export {initialState as zadCustomerRefundInitialState}
